import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { societyApi } from '@/features/societies/api/societyApi';
import type { SocietyFilters } from '@/features/societies/types/society.types';

/**
 * Hooks React Query partagés pour la gestion des sociétés
 * Utilisés par admin et outil-pro
 */

// ==================== QUERIES (lecture) ====================

/**
 * Liste des sociétés avec filtres
 */
export const useSocieties = (filters?: SocietyFilters, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['societies', filters],
    queryFn: () => societyApi.getSocieties(filters),
    enabled: options?.enabled ?? true,
    staleTime: 30000, // 30 secondes
  });
};

/**
 * Détail d'une société
 */
export const useSociety = (id: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['society', id],
    queryFn: () => societyApi.getSociety(id!),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 30000,
  });
};

/**
 * Enfants directs d'une société
 */
export const useSocietyChildren = (id: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['society-children', id],
    queryFn: () => societyApi.getSocietyChildren(id!),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 30000,
  });
};

/**
 * Vérifie si au moins un enfant actif a un stripeAccountId dans sa configuration
 */
export const useChildrenHaveStripe = (id: string | undefined, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: ['society-children-have-stripe', id],
    queryFn: () => societyApi.getChildrenHaveStripe(id!),
    enabled: !!id && (options?.enabled ?? true),
    staleTime: 30000,
  });
};

/**
 * Ancêtres d'une société
 */
export const useSocietyAncestors = (id: string | undefined) => {
  return useQuery({
    queryKey: ['society-ancestors', id],
    queryFn: () => societyApi.getSocietyAncestors(id!),
    enabled: !!id,
    staleTime: 60000,
  });
};

/**
 * Descendants d'une société
 */
export const useSocietyDescendants = (id: string | undefined) => {
  return useQuery({
    queryKey: ['society-descendants', id],
    queryFn: () => societyApi.getSocietyDescendants(id!),
    enabled: !!id,
    staleTime: 30000,
  });
};

/**
 * Logs d'audit d'une société
 */
export const useSocietyAuditLogs = (id: string | undefined, limit = 50) => {
  return useQuery({
    queryKey: ['society-audit-logs', id, limit],
    queryFn: () => societyApi.getSocietyAuditLogs(id!, limit),
    enabled: !!id,
    staleTime: 30000,
  });
};

// ==================== MUTATIONS (écriture) ====================

/**
 * Mettre à jour une société
 */
export const useUpdateSociety = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, society }: { id: string; society: Record<string, any> }) =>
      societyApi.updateSociety(id, society),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['society', id] });
      queryClient.invalidateQueries({ queryKey: ['societies'] });
    },
  });
};

/**
 * Activer une société
 */
export const useActivateSociety = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => societyApi.activateSociety(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['society', id] });
      queryClient.invalidateQueries({ queryKey: ['societies'] });
    },
  });
};

/**
 * Désactiver une société
 */
export const useDeactivateSociety = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => societyApi.deactivateSociety(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['society', id] });
      queryClient.invalidateQueries({ queryKey: ['societies'] });
    },
  });
};
